/**
 * Custom hooks per Knowledge Bases
 */
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPostFormData, apiPut } from "@/lib/api-client";
import type {
    KnowledgeBaseCreate,
    KnowledgeBaseListFilters,
    KnowledgeBaseResponse,
    KnowledgeBaseUpdate,
    KnowledgeDocumentDetailResponse,
    KnowledgeDocumentResponse,
    KnowledgeDocumentUpdateRequest,
    KnowledgeDocumentUploadOptions,
    RegenerateEmbeddingsResponse,
} from "@/types/api/knowledge-bases";

const KNOWLEDGE_BASES_QUERY_KEYS = {
    all: ["knowledge-bases"] as const,
    lists: () => [...KNOWLEDGE_BASES_QUERY_KEYS.all, "list"] as const,
    list: (filters?: KnowledgeBaseListFilters) => [...KNOWLEDGE_BASES_QUERY_KEYS.lists(), filters] as const,
    details: () => [...KNOWLEDGE_BASES_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...KNOWLEDGE_BASES_QUERY_KEYS.details(), id] as const,
    documents: (kbId: string) => [...KNOWLEDGE_BASES_QUERY_KEYS.detail(kbId), "documents"] as const,
    document: (kbId: string, docId: string) => [...KNOWLEDGE_BASES_QUERY_KEYS.documents(kbId), docId] as const,
};

/**
 * Hook per lista knowledge bases con filtri
 */
export function useKnowledgeBases(filters?: KnowledgeBaseListFilters, options?: Omit<UseQueryOptions<KnowledgeBaseResponse[], Error>, "queryKey" | "queryFn">) {
    return useQuery({
        queryKey: KNOWLEDGE_BASES_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<KnowledgeBaseResponse[]> => {
            const response = await apiGet<{ success: boolean; data: KnowledgeBaseResponse[]; count: number }>("/knowledge-bases", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
            return response.data || [];
        },
        ...options,
    });
}

/**
 * Hook per singola knowledge base
 */
export function useKnowledgeBase(knowledgeBaseId: string) {
    return useQuery({
        queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(knowledgeBaseId),
        queryFn: async (): Promise<KnowledgeBaseResponse> => {
            const response = await apiGet<{ success: boolean; data: KnowledgeBaseResponse }>(`/knowledge-bases/${knowledgeBaseId}`);
            return response.data;
        },
        enabled: !!knowledgeBaseId,
    });
}

/**
 * Hook per creare knowledge base
 */
export function useCreateKnowledgeBase() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: KnowledgeBaseCreate): Promise<KnowledgeBaseResponse> => {
            const response = await apiPost<{ success: boolean; data: KnowledgeBaseResponse }>("/knowledge-bases", data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per aggiornare knowledge base
 */
export function useUpdateKnowledgeBase() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ knowledgeBaseId, data }: { knowledgeBaseId: string; data: KnowledgeBaseUpdate }): Promise<KnowledgeBaseResponse> => {
            const response = await apiPut<{ success: boolean; data: KnowledgeBaseResponse }>(`/knowledge-bases/${knowledgeBaseId}`, data);
            return response.data;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(variables.knowledgeBaseId) });
        },
    });
}

/**
 * Hook per eliminare knowledge base
 */
export function useDeleteKnowledgeBase() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (knowledgeBaseId: string): Promise<void> => {
            return await apiDelete<void>(`/knowledge-bases/${knowledgeBaseId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per lista documenti cross-KB (tutti i documenti accessibili)
 */
export function useAllKnowledgeDocuments() {
    return useQuery({
        queryKey: [...KNOWLEDGE_BASES_QUERY_KEYS.all, "all-documents"] as const,
        queryFn: async (): Promise<KnowledgeDocumentResponse[]> => {
            const response = await apiGet<{ success: boolean; data: KnowledgeDocumentResponse[]; count: number }>(
                "/knowledge-bases/all-documents",
            );
            return response.data || [];
        },
    });
}

/**
 * Hook per lista documenti di una knowledge base
 */
export function useKnowledgeBaseDocuments(knowledgeBaseId: string) {
    return useQuery({
        queryKey: KNOWLEDGE_BASES_QUERY_KEYS.documents(knowledgeBaseId),
        queryFn: async (): Promise<KnowledgeDocumentResponse[]> => {
            const response = await apiGet<{ success: boolean; data: KnowledgeDocumentResponse[]; count: number }>(
                `/knowledge-bases/${knowledgeBaseId}/documents`,
            );
            return response.data || [];
        },
        enabled: !!knowledgeBaseId,
    });
}

/**
 * Hook per upload documento a knowledge base
 */
export function useUploadKnowledgeDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            knowledgeBaseId,
            file,
            options,
        }: {
            knowledgeBaseId: string;
            file: File;
            options?: KnowledgeDocumentUploadOptions;
        }): Promise<KnowledgeDocumentResponse> => {
            const formData = new FormData();
            formData.append("file", file);

            // Add optional parameters if provided
            if (options?.priority !== undefined) {
                formData.append("priority", options.priority.toString());
            }
            if (options?.access_level) {
                formData.append("access_level", options.access_level);
            }
            if (options?.chunking_params) {
                if (options.chunking_params.chunk_size !== undefined) {
                    formData.append("chunk_size", options.chunking_params.chunk_size.toString());
                }
                if (options.chunking_params.chunk_overlap !== undefined) {
                    formData.append("chunk_overlap", options.chunking_params.chunk_overlap.toString());
                }
            }

            const response = await apiPostFormData<{ success: boolean; data: KnowledgeDocumentResponse }>(
                `/knowledge-bases/${knowledgeBaseId}/documents`,
                formData,
            );
            return response.data;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.documents(variables.knowledgeBaseId) });
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(variables.knowledgeBaseId) });
        },
    });
}

/**
 * Hook per eliminare documento da knowledge base
 */
export function useDeleteKnowledgeDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ knowledgeBaseId, documentId }: { knowledgeBaseId: string; documentId: string }): Promise<void> => {
            return await apiDelete<void>(`/knowledge-bases/${knowledgeBaseId}/documents/${documentId}`);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.documents(variables.knowledgeBaseId) });
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(variables.knowledgeBaseId) });
        },
    });
}

/**
 * Hook per singolo documento con dettagli completi e chunks
 */
export function useKnowledgeDocument(knowledgeBaseId: string, docId: string) {
    return useQuery({
        queryKey: KNOWLEDGE_BASES_QUERY_KEYS.document(knowledgeBaseId, docId),
        queryFn: async (): Promise<KnowledgeDocumentDetailResponse> => {
            const response = await apiGet<{ success: boolean; data: KnowledgeDocumentDetailResponse }>(
                `/knowledge-bases/${knowledgeBaseId}/documents/${docId}`,
                { params: { include_chunks: true } },
            );
            return response.data;
        },
        enabled: !!knowledgeBaseId && !!docId,
    });
}

/**
 * Hook per aggiornare metadati documento
 */
export function useUpdateKnowledgeDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            knowledgeBaseId,
            documentId,
            data,
        }: {
            knowledgeBaseId: string;
            documentId: string;
            data: KnowledgeDocumentUpdateRequest;
        }): Promise<KnowledgeDocumentDetailResponse> => {
            const response = await apiPut<{ success: boolean; data: KnowledgeDocumentDetailResponse }>(
                `/knowledge-bases/${knowledgeBaseId}/documents/${documentId}`,
                data,
            );
            return response.data;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.documents(variables.knowledgeBaseId) });
            queryClient.invalidateQueries({
                queryKey: KNOWLEDGE_BASES_QUERY_KEYS.document(variables.knowledgeBaseId, variables.documentId),
            });
        },
    });
}

/**
 * Hook per rigenerare embeddings di un documento
 */
export function useRegenerateEmbeddings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            knowledgeBaseId,
            documentId,
        }: {
            knowledgeBaseId: string;
            documentId: string;
        }): Promise<RegenerateEmbeddingsResponse> => {
            const response = await apiPost<{ success: boolean; data: RegenerateEmbeddingsResponse }>(
                `/knowledge-bases/${knowledgeBaseId}/documents/${documentId}/regenerate-embeddings`,
            );
            return response.data;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.documents(variables.knowledgeBaseId) });
            queryClient.invalidateQueries({
                queryKey: KNOWLEDGE_BASES_QUERY_KEYS.document(variables.knowledgeBaseId, variables.documentId),
            });
            queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(variables.knowledgeBaseId) });
        },
    });
}
